import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { getPreference } from '@atlassian/jira-business-preferences/src/services/view-preferences/index.tsx';
import type { SortingAttributes } from '@atlassian/jira-business-common/src/common/types/sorting.tsx';
import type { FieldsDetailPreference } from '@atlassian/jira-business-preferences/src/types.tsx';
import { sortDirections, PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';

type ProjectKey = string;

const DEFAULT_LIST_VIEW_ORDER_FIELD = 'rank';
const DEFAULT_LIST_VIEW_SORT_DIRECTION = sortDirections.ASC;
const PROJECT_ID_REGEX =
	/^__storejs_jwm-view-preferences_(\d+)_list_(fieldsDetail|filter|groupBy)$/;

export type MigrationData = {
	projectId: string;
	groupBy: string | null;
	filter: string | null;
	sorting: SortingAttributes | null;
};

export type PaidMigrationData = MigrationData & {
	fieldsDetail: FieldsDetailPreference | null;
};

const parsePreferenceValue = (value: unknown): string | null => {
	if (typeof value === 'string') return value;
	if (value === null || value === undefined) return null;
	try {
		return JSON.stringify(value);
	} catch {
		return String(value);
	}
};

const parseSortingPreferenceValue = (value: SortingAttributes): SortingAttributes | null => {
	if (typeof value !== 'object' || value === null) {
		return null;
	}
	return value ?? null;
};

export const getMigrationDataForProject = (projectId: string): MigrationData => {
	try {
		const groupByValue = getPreference(projectId, 'list', 'groupBy');
		const filterValue = getPreference(projectId, 'list', 'filter');
		const sortingValue = getPreference(projectId, 'list', 'sorting');
		return {
			projectId,
			groupBy: parsePreferenceValue(groupByValue),
			filter: parsePreferenceValue(filterValue),
			sorting: parseSortingPreferenceValue(sortingValue),
		};
	} catch (error) {
		return {
			projectId,
			groupBy: null,
			filter: null,
			sorting: null,
		};
	}
};

export const getPaidMigrationDataForProject = (projectId: string): PaidMigrationData => {
	try {
		const groupByValue = getPreference(projectId, 'list', 'groupBy');
		const filterValue = getPreference(projectId, 'list', 'filter');
		const sortingValue = getPreference(projectId, 'list', 'sorting');
		const fieldsDetailValue = getPreference(projectId, 'list', 'fieldsDetail');
		return {
			projectId,
			groupBy: groupByValue,
			filter: filterValue,
			sorting: sortingValue,
			fieldsDetail: fieldsDetailValue,
		};
	} catch (error) {
		return {
			projectId,
			groupBy: null,
			filter: null,
			sorting: null,
			fieldsDetail: null,
		};
	}
};

export const hasMigrationDataForProject = (projectId: string): boolean => {
	const migrationData = getMigrationDataForProject(projectId);
	return (
		migrationData.groupBy !== null ||
		migrationData.filter !== null ||
		migrationData.sorting !== null
	);
};

export const getDefaultListViewJqlWithFilter = (projectKey: ProjectKey, filter: string) =>
	`project = "${projectKey}" AND ${filter} ORDER BY ${DEFAULT_LIST_VIEW_ORDER_FIELD} ${DEFAULT_LIST_VIEW_SORT_DIRECTION}`;

export const getDefaultListViewJql = (projectKey: ProjectKey) =>
	`project = "${projectKey}" ORDER BY ${DEFAULT_LIST_VIEW_ORDER_FIELD} ${DEFAULT_LIST_VIEW_SORT_DIRECTION}`;

export const getDefaultListViewJqlWithSorting = (
	projectKey: ProjectKey,
	sorting: SortingAttributes,
) =>
	`project = "${projectKey}" ORDER BY ${sorting.sortBy} ${sorting.direction}, ${DEFAULT_LIST_VIEW_ORDER_FIELD} ${sorting.direction}`;

export const getDefaultListViewJqlWithFilterAndSorting = (
	projectKey: ProjectKey,
	filter: string,
	sorting: SortingAttributes,
) =>
	`project = "${projectKey}" AND ${filter} ORDER BY ${sorting.sortBy} ${sorting.direction}, ${DEFAULT_LIST_VIEW_ORDER_FIELD} ${sorting.direction}`;

export const getJqlFromMigrationData = (migrationData: MigrationData, projectKey: string) => {
	const { filter, sorting } = migrationData;

	if (filter && sorting) {
		return getDefaultListViewJqlWithFilterAndSorting(projectKey, filter, sorting);
	}

	if (filter) {
		return getDefaultListViewJqlWithFilter(projectKey, filter);
	}

	if (sorting) {
		return getDefaultListViewJqlWithSorting(projectKey, sorting);
	}

	return getDefaultListViewJql(projectKey);
};

// ----------------------- Paid Changboarding Utils -----------------------
// Following utiliies construct JQL without project scoping; Project scoping will be completed by the BE.
const getPaidJqlWithFilterAndSorting = (filter: string, sorting: SortingAttributes) =>
	`AND ${filter} ORDER BY ${sorting.sortBy} ${sorting.direction}, ${DEFAULT_LIST_VIEW_ORDER_FIELD} ${sorting.direction}`;

const getPaidJqlWithFilter = (filter: string) =>
	`AND ${filter} ORDER BY ${DEFAULT_LIST_VIEW_ORDER_FIELD} ${DEFAULT_LIST_VIEW_SORT_DIRECTION}`;

const getPaidJqlWithSorting = (sorting: SortingAttributes) =>
	`ORDER BY ${sorting.sortBy} ${sorting.direction}, ${DEFAULT_LIST_VIEW_ORDER_FIELD} ${sorting.direction}`;

const getDefaultPaidJql = () =>
	`ORDER BY ${DEFAULT_LIST_VIEW_ORDER_FIELD} ${DEFAULT_LIST_VIEW_SORT_DIRECTION}`;

export const getPaidJqlFromMigrationData = (migrationData: PaidMigrationData) => {
	const { filter, sorting } = migrationData;

	if (filter && sorting) {
		return getPaidJqlWithFilterAndSorting(filter, sorting);
	}
	if (filter) {
		return getPaidJqlWithFilter(filter);
	}

	if (sorting) {
		return getPaidJqlWithSorting(sorting);
	}

	return getDefaultPaidJql();
};

export const getConfiguredProjectIdsFromLocalStorage = (): string[] => {
	try {
		return Array.from(
			new Set(
				Object.keys(localStorage)
					.map((key) => key.match(PROJECT_ID_REGEX)?.[1])
					.filter((id) => id !== undefined),
			),
		);
	} catch (error) {
		fireErrorAnalytics({
			meta: {
				id: 'paidListNINConfigMigration',
				packageName: PACKAGE_NAME,
				teamName: TEAM_NAME,
			},
			error:
				error instanceof Error
					? error
					: new Error('Failed to get configured project ids from localStorage'),
			sendToPrivacyUnsafeSplunk: true,
		});
		return [];
	}
};
